import React, { useCallback, useEffect, useState } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import TablePagination from '../../components/TablePagination';
import ProjectNameText from '../../components/ProjectNameText';
import StatusBadge from '../../components/StatusBadge';
import { submittalsAPI } from '../../services/authService';
import { formatDateIN } from '../../utils/dateFormat';
import SubmittalStatusBadge, { formatSubmittalStatus } from './SubmittalStatusBadge';

const STATUS_OPTIONS = [
  'all', 'DRAFT', 'INTERNAL_REVIEW', 'PENDING_CONSULTANT_REVIEW', 'PENDING_CLIENT_APPROVAL',
  'RETURNED_BY_CONSULTANT', 'RETURNED_BY_CLIENT', 'APPROVED', 'APPROVED_WITH_COMMENTS', 'REJECTED', 'CLOSED',
];

const PRIORITY_OPTIONS = [
  { value: 'all', label: 'All Priority' },
  { value: 'LOW', label: 'Low' },
  { value: 'NORMAL', label: 'Routine' },
  { value: 'HIGH', label: 'Urgent' },
  { value: 'CRITICAL', label: 'Critical' },
];

const formatPriorityLabel = (priority) => {
  const key = String(priority || 'NORMAL').toUpperCase();
  if (key === 'LOW') return 'Low';
  if (key === 'HIGH') return 'Urgent';
  if (key === 'CRITICAL') return 'Critical';
  return 'Routine';
};

const getPriorityTone = (priority) => {
  const key = String(priority || 'NORMAL').toUpperCase();
  if (key === 'CRITICAL') return 'critical';
  if (key === 'HIGH') return 'medium';
  if (key === 'LOW') return 'closed';
  return 'low';
};

const SubmittalRegister = ({ onViewSubmittal, onCreateNew }) => {
  const { projects } = useProjects();
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dateRangePreset, setDateRangePreset] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [searchParam, setSearchParam] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProjectSummary, setShowProjectSummary] = useState(false);
  const [projectSummaryRows, setProjectSummaryRows] = useState([]);
  const [projectSummaryTotals, setProjectSummaryTotals] = useState({
    pendingSubmittals: 0,
    approvedSubmittals: 0,
    totalSubmittals: 0,
  });
  const [projectSummaryAsOn, setProjectSummaryAsOn] = useState('');
  const [projectSummaryLoading, setProjectSummaryLoading] = useState(false);
  const perPage = 50;

  useEffect(() => {
    submittalsAPI.getCategories()
      .then((resp) => setCategories(safeArray(resp?.data?.data)))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { page, pageSize: perPage };
        if (projectFilter !== 'all') params.projectId = projectFilter;
        if (statusFilter !== 'all') params.status = statusFilter;
        if (categoryFilter !== 'all') params.categoryId = categoryFilter;
        if (priorityFilter !== 'all') params.priority = priorityFilter;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        if (searchParam.trim()) params.search = searchParam.trim();
        const resp = await submittalsAPI.getAll(params);
        const { rows: dataRows, totalCount: cnt } = unwrapListResponse(resp);
        if (!cancelled) {
          setRows(safeArray(dataRows));
          setTotalCount(cnt);
        }
      } catch (err) {
        console.warn('Submittal register load failed:', err?.message || err);
        if (!cancelled) { setRows([]); setTotalCount(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [page, projectFilter, statusFilter, categoryFilter, priorityFilter, dateFrom, dateTo, searchParam]);

  const loadProjectSummary = useCallback(async () => {
    setProjectSummaryLoading(true);
    try {
      const params = projectFilter !== 'all' ? { projectId: projectFilter } : {};
      const resp = await submittalsAPI.getSummary(params);
      const data = resp?.data || {};
      setProjectSummaryRows(safeArray(data.projectSummary));
      setProjectSummaryTotals(data.projectSummaryTotals || {
        pendingSubmittals: 0,
        approvedSubmittals: 0,
        totalSubmittals: 0,
      });
      setProjectSummaryAsOn(String(data.asOn || '').slice(0, 10));
    } catch (_err) {
      setProjectSummaryRows([]);
      setProjectSummaryTotals({ pendingSubmittals: 0, approvedSubmittals: 0, totalSubmittals: 0 });
      setProjectSummaryAsOn('');
    } finally {
      setProjectSummaryLoading(false);
    }
  }, [projectFilter]);

  useEffect(() => {
    loadProjectSummary();
  }, [loadProjectSummary]);

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const showProjectColumn = projectFilter === 'all';

  const applyRegisterSearch = () => {
    setSearchParam(search.trim());
    setPage(1);
  };

  const clearRegisterSearch = () => {
    setSearch('');
    setSearchParam('');
    setPage(1);
  };

  const resetFilters = () => {
    setProjectFilter('all');
    setStatusFilter('all');
    setCategoryFilter('all');
    setPriorityFilter('all');
    setDateRangePreset('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setSearchParam('');
    setPage(1);
  };

  const handleDownloadPdf = () => {
    window.print();
  };

  return (
    <div>
      <div className="page-header-frozen">
        <div className="section-header">
          <h2>
            Submittal Register
            <span className="count-badge">{totalCount}</span>
          </h2>
          <div className="correspondence-register-header-actions">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn-secondary" onClick={handleDownloadPdf}>
                Download PDF
              </button>
              {typeof onCreateNew === 'function' && (
                <button type="button" className="btn-primary" onClick={onCreateNew}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Create Submittal
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="correspondence-search-hero">
          <div className="correspondence-search-hero-row">
            <div className="search-hero-inner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                placeholder="Search by subject or submittal number…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyRegisterSearch();
                  }
                }}
                aria-label="Search submittals by subject or submittal number"
              />
            </div>
            <button type="button" className="btn-primary correspondence-search-submit" onClick={applyRegisterSearch}>
              Search
            </button>
            {(search || searchParam) && (
              <button type="button" className="btn-secondary correspondence-search-clear" onClick={clearRegisterSearch}>
                Clear
              </button>
            )}
          </div>
          {searchParam && (
            <p className="correspondence-search-active" aria-live="polite">
              Showing results for &ldquo;{searchParam}&rdquo;
            </p>
          )}
        </div>

        <div className="filter-bar" style={{ marginBottom: 16 }}>
          <ProjectFilterSelect
            value={projectFilter}
            onChange={(value) => { setProjectFilter(value); setPage(1); }}
            projects={projects}
          />
          <select
            value={dateRangePreset}
            onChange={(e) => {
              const val = e.target.value;
              setDateRangePreset(val);
              if (val === '') {
                setDateFrom('');
                setDateTo('');
              } else {
                const days = parseInt(val, 10);
                const to = new Date();
                const from = new Date(to);
                from.setDate(from.getDate() - (days - 1));
                setDateFrom(from.toISOString().slice(0, 10));
                setDateTo(to.toISOString().slice(0, 10));
              }
              setPage(1);
            }}
            title="Quick date range by created date"
          >
            <option value="">Date range</option>
            <option value="7">Last 7 days</option>
            <option value="15">Last 15 days</option>
            <option value="30">Last 30 days</option>
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateRangePreset(''); setDateFrom(e.target.value); setPage(1); }}
            title="Created date from"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateRangePreset(''); setDateTo(e.target.value); setPage(1); }}
            title="Created date to"
          />
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s === 'all' ? 'All Status' : formatSubmittalStatus(s)}</option>
            ))}
          </select>
          <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}>
            <option value="all">All Categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}>
            {PRIORITY_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <ResetFiltersButton onClick={resetFilters} />
        </div>
      </div>

      <div className="table-container" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px 6px' }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>ABSTRACT OF ALL PROJECTS - SUBMITTALS</h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#334155' }}>As on: {projectSummaryAsOn ? formatDateIN(projectSummaryAsOn) : '-'}</span>
            <button
              type="button"
              className="btn-sm"
              onClick={() => setShowProjectSummary((v) => !v)}
              style={{
                minWidth: 92,
                background: '#7C3AED',
                border: '1px solid #7C3AED',
                color: '#fff',
                fontWeight: 600,
              }}
            >
              {showProjectSummary ? 'Collapse' : 'Expand'}
            </button>
          </div>
        </div>
        {showProjectSummary && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Sl.No.</th>
                <th>Name of Project</th>
                <th>Department</th>
                <th>Pending Submittals</th>
                <th>Approved Submittals</th>
                <th>Total Submittals</th>
              </tr>
            </thead>
            <tbody>
              {projectSummaryLoading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>
                    Loading summary...
                  </td>
                </tr>
              ) : projectSummaryRows.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>
                    No summary data.
                  </td>
                </tr>
              ) : (
                <>
                  {projectSummaryRows.map((row, idx) => (
                    <tr key={`submittal-proj-summary-${row.projectId || idx}`}>
                      <td>{idx + 1}</td>
                      <td>{row.projectName}</td>
                      <td>{row.departmentCode || '-'}</td>
                      <td>{Number(row.pendingSubmittals || 0)}</td>
                      <td>{Number(row.approvedSubmittals || 0)}</td>
                      <td style={{ fontWeight: 700 }}>{Number(row.totalSubmittals || 0)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'right', fontWeight: 700 }}>Total</td>
                    <td style={{ fontWeight: 700 }}>{Number(projectSummaryTotals.pendingSubmittals || 0)}</td>
                    <td style={{ fontWeight: 700 }}>{Number(projectSummaryTotals.approvedSubmittals || 0)}</td>
                    <td style={{ fontWeight: 700 }}>{Number(projectSummaryTotals.totalSubmittals || 0)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <TablePagination
          className="pagination pagination--register-header"
          page={page}
          totalPages={totalPages}
          onPrevious={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
        />
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Download</th>
              <th>Submittal Date</th>
              <th>Submittal No.</th>
              <th>Subject</th>
              <th>Category</th>
              {showProjectColumn && <th>Project</th>}
              <th>Revision</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Aging</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={showProjectColumn ? 10 : 9} style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  Loading submittals...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={showProjectColumn ? 10 : 9} style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  No submittals found matching the filters.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const submittalDate = row.submitted_at || row.created_at;
                return (
                  <tr
                    key={row.id}
                    className={typeof onViewSubmittal === 'function' ? 'clickable' : ''}
                    onClick={() => onViewSubmittal?.(row.id)}
                  >
                    <td>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewSubmittal?.(row.id);
                        }}
                      >
                        View
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <span>{formatDateIN(submittalDate)}</span>
                        {row.is_overdue && (
                          <span
                            style={{
                              padding: '1px 7px',
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 700,
                              color: '#ffffff',
                              background: '#dc2626',
                              display: 'inline-block',
                              lineHeight: 1.2,
                            }}
                          >
                            Overdue
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word', fontSize: 12, color: '#374151', fontWeight: 600 }}>
                      {row.submittal_no || row.base_number || `#${row.id}`}
                    </td>
                    <td style={{ fontWeight: 500, maxWidth: 320, whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: 1.4 }}>
                      {row.subject}
                    </td>
                    <td><span className="category-tag">{row.category?.name || '—'}</span></td>
                    {showProjectColumn && (
                      <td style={{ fontSize: 12, color: '#374151', maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <ProjectNameText as="span">{row.project?.name || '—'}</ProjectNameText>
                      </td>
                    )}
                    <td>{row.current_revision_label || 'R0'}</td>
                    <td><StatusBadge tone={getPriorityTone(row.priority)}>{formatPriorityLabel(row.priority)}</StatusBadge></td>
                    <td><SubmittalStatusBadge status={row.status} isOverdue={row.is_overdue} showOverdueFlag={false} /></td>
                    <td>{row.aging_days ?? 0}d</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPrevious={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
        />
      </div>
    </div>
  );
};

export default SubmittalRegister;
